/**
 * Confirmation / Booking Status Design Preview — mock ViewModels only.
 * No API / booking / payment integration.
 */

export const CONFIRMATION_PREVIEW_STATES = [
  { id: "booked", label: "A · Booked / Pending" },
  { id: "held", label: "B · Held" },
  { id: "pending-ticket", label: "C · Pending Ticketing" },
  { id: "ticketed", label: "D · Ticketed" },
  { id: "bank-pending", label: "E · Bank Transfer Pending" },
  { id: "failed", label: "F · Failed" },
];

const SHARED_FLIGHT = {
  airlineLabel: "Arkia",
  fareFamily: "CHARTER",
  routeLabel: "TLV → LCA",
  datesLabel: "08/10–11/10",
  passengersLabel: "2 נוסעים",
  totalLabel: "₪1,395",
};

const SHARED_PASSENGERS = [
  { id: "pax-1", name: "David Cohen" },
  { id: "pax-2", name: "Noa Cohen" },
];

const SHARED_SECONDARY = [
  { id: "send", label: "שלח ללקוח" },
  { id: "docs", label: "מסמכים" },
  { id: "home", label: "חזרה למערכת" },
];

function step(label, value, tone) {
  return { label, value, tone };
}

/**
 * @param {string} stateId
 */
export function getMockConfirmationViewModel(stateId = "booked") {
  const id = CONFIRMATION_PREVIEW_STATES.some((s) => s.id === stateId)
    ? stateId
    : "booked";

  const base = {
    title: "אישור הזמנה",
    flight: SHARED_FLIGHT,
    passengers: SHARED_PASSENGERS,
  };

  switch (id) {
    case "held":
      return {
        ...base,
        stateId: id,
        tone: "amber",
        header: {
          title: "ההזמנה נשמרה ללא כרטוס",
          reference: "HR-88221045",
          badgeLabel: "Hold",
          badgeTone: "amber",
          copyLabel: "העתק",
        },
        timeline: [
          step("הזמנה", "Hold ✓", "amber"),
          step("תשלום", "ממתין", "amber"),
          step("כרטוס", "ממתין", "muted"),
        ],
        next: {
          title: "מה קורה עכשיו?",
          body: "יש להשלים תשלום עד 18:30 כדי לשמור את ההזמנה.",
        },
        hold: {
          expiresLabel: "Hold expires",
          expiresValue: "היום 18:30",
          priceGuaranteeLabel: "Price guarantee",
          priceGuaranteeValue: "לא",
          amountDueLabel: "Amount due",
          amountDueValue: "₪1,395",
          warning:
            "אם לא יבוצע תשלום עד המועד, ה-Hold עלול לפוג.",
        },
        payment: {
          methodLabel: "אמצעי תשלום",
          methodValue: "—",
          amountLabel: "סכום",
          amountValue: "₪1,395",
          statusLabel: "סטטוס",
          statusValue: "ממתין",
          statusTone: "amber",
          splits: null,
        },
        tickets: null,
        primaryAction: { id: "pay", label: "המשך לתשלום" },
        secondaryActions: SHARED_SECONDARY,
      };

    case "pending-ticket":
      return {
        ...base,
        stateId: id,
        tone: "blue",
        header: {
          title: "ההזמנה אושרה — הכרטוס ממתין",
          reference: "HR-12345678",
          badgeLabel: "ממתין לכרטוס",
          badgeTone: "blue",
          copyLabel: "העתק",
        },
        timeline: [
          step("הזמנה", "✓", "green"),
          step("תשלום", "✓", "green"),
          step("כרטוס", "ממתין", "blue"),
        ],
        next: {
          title: "מה קורה עכשיו?",
          body:
            "אין צורך בתשלום נוסף. הכרטוס נמצא בטיפול והסטטוס יתעדכן לאחר הנפקת הכרטיסים.",
        },
        hold: null,
        payment: {
          methodLabel: "אמצעי תשלום",
          methodValue: "כרטיס אשראי",
          amountLabel: "סכום",
          amountValue: "₪1,395",
          statusLabel: "סטטוס",
          statusValue: "שולם ✓",
          statusTone: "green",
          splits: [
            { label: "כרטיס 1", amount: "₪700", status: "שולם ✓", tone: "green" },
            { label: "כרטיס 2", amount: "₪695", status: "שולם ✓", tone: "green" },
          ],
        },
        tickets: null,
        primaryAction: { id: "view", label: "הצג הזמנה" },
        secondaryActions: SHARED_SECONDARY,
      };

    case "ticketed":
      return {
        ...base,
        stateId: id,
        tone: "green",
        header: {
          title: "ההזמנה הושלמה והכרטיסים הונפקו",
          reference: "HR-12345678",
          badgeLabel: "Ticketed",
          badgeTone: "green",
          copyLabel: "העתק",
        },
        timeline: [
          step("הזמנה", "✓", "green"),
          step("תשלום", "✓", "green"),
          step("כרטוס", "✓", "green"),
        ],
        next: {
          title: "מה קורה עכשיו?",
          body: "הכרטיסים הונפקו וניתן לשלוח אותם ללקוח.",
        },
        hold: null,
        tickets: [
          {
            name: "David Cohen",
            ticketLabel: "מספר כרטיס",
            ticketNumber: "123-XXXXXXXXXX",
          },
          {
            name: "Noa Cohen",
            ticketLabel: "מספר כרטיס",
            ticketNumber: "123-XXXXXXXXXX",
          },
        ],
        payment: {
          methodLabel: "אמצעי תשלום",
          methodValue: "כרטיס אשראי",
          amountLabel: "סכום",
          amountValue: "₪1,395",
          statusLabel: "סטטוס",
          statusValue: "שולם ✓",
          statusTone: "green",
          splits: null,
        },
        primaryAction: { id: "view", label: "הצג הזמנה" },
        secondaryActions: SHARED_SECONDARY,
      };

    case "bank-pending":
      return {
        ...base,
        stateId: id,
        tone: "amber",
        header: {
          title: "ההזמנה ממתינה לאישור תשלום",
          reference: "HR-55443322",
          badgeLabel: "ממתין לאישור תשלום",
          badgeTone: "amber",
          copyLabel: "העתק",
        },
        timeline: [
          step("הזמנה", "נוצרה · ממתינה", "amber"),
          step("תשלום", "ממתין לאישור העברה", "amber"),
          step("כרטוס", "לא בוצע", "muted"),
        ],
        next: {
          title: "מה קורה עכשיו?",
          body: "ההזמנה תאושר לאחר אימות ההעברה.",
        },
        hold: null,
        bankPending: {
          amountLabel: "Amount",
          amountValue: "₪1,395",
          note: "לאחר אישור ההעברה ימשיך תהליך הכרטוס.",
        },
        payment: {
          methodLabel: "אמצעי תשלום",
          methodValue: "העברה בנקאית",
          amountLabel: "סכום",
          amountValue: "₪1,395",
          statusLabel: "סטטוס",
          statusValue: "ממתין לאישור",
          statusTone: "amber",
          splits: null,
        },
        tickets: null,
        primaryAction: { id: "view", label: "הצג הזמנה" },
        secondaryActions: SHARED_SECONDARY,
      };

    case "failed":
      return {
        ...base,
        stateId: id,
        tone: "red",
        header: {
          title: "ההזמנה לא הושלמה",
          reference: null,
          badgeLabel: "Failed",
          badgeTone: "red",
          copyLabel: null,
        },
        timeline: [
          step("הזמנה", "לא נוצרה", "red"),
          step("תשלום", "failed / not charged", "red"),
          step("כרטוס", "לא בוצע", "muted"),
        ],
        next: {
          title: "מה קורה עכשיו?",
          body: "לא בוצעה הזמנה סופית. ניתן לנסות שוב.",
        },
        hold: null,
        failed: {
          paymentLabel: "Payment",
          paymentValue: "failed / not charged",
          bookingLabel: "Booking",
          bookingValue: "not created / failed",
          note: "לא נוצר מספר הזמנה. לא בוצע חיוב.",
        },
        payment: null,
        tickets: null,
        primaryAction: { id: "retry", label: "נסה שוב" },
        secondaryActions: [
          { id: "payment", label: "חזרה לתשלום" },
          { id: "results", label: "חזרה לתוצאות" },
        ],
      };

    case "booked":
    default:
      return {
        ...base,
        stateId: "booked",
        tone: "blue",
        header: {
          title: "ההזמנה אושרה — הכרטוס ממתין",
          reference: "HR-12345678",
          badgeLabel: "מאושרת · ממתין לכרטוס",
          badgeTone: "blue",
          copyLabel: "העתק",
        },
        timeline: [
          step("הזמנה", "✓", "green"),
          step("תשלום", "✓", "green"),
          step("כרטוס", "ממתין", "blue"),
        ],
        next: {
          title: "מה קורה עכשיו?",
          body:
            "אין צורך בתשלום נוסף. הכרטוס נמצא בטיפול והסטטוס יתעדכן לאחר הנפקת הכרטיסים.",
        },
        hold: null,
        payment: {
          methodLabel: "אמצעי תשלום",
          methodValue: "כרטיס אשראי",
          amountLabel: "סכום",
          amountValue: "₪1,395",
          statusLabel: "סטטוס",
          statusValue: "שולם ✓",
          statusTone: "green",
          splits: null,
        },
        tickets: null,
        primaryAction: { id: "view", label: "הצג הזמנה" },
        secondaryActions: SHARED_SECONDARY,
      };
  }
}
